import { useState } from 'react';
import { ensayoAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FlaskConical, Loader2, CheckCircle2, XCircle, Trash2, AlertTriangle, Info,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Ensayar al agente que estás configurando: escribir un mensaje y ver **qué hizo**.
 *
 * **Por que existe.** Agregar una skill, una herramienta o una conexion y no poder observar si
 * funciona convierte la configuracion en adivinanza. La unica forma de validar era escribir un
 * correo real y leer la autopsia: ensucia un entorno compartido, puede avisarle a una persona
 * de verdad, y no se puede repetir.
 *
 * **Lo que muestra no es la respuesta: es el diagnostico.** Cuando una herramienta no se
 * dispara, el texto de la respuesta no dice por que. Lo dicen las herramientas que llamo, las
 * skills que recibio y los presupuestos del turno — en ese orden de utilidad.
 */
export default function EnsayarAgente({ tenantId, capsuleId, agente }) {
  const [mensaje, setMensaje] = useState('');
  const [corriendo, setCorriendo] = useState(false);
  const [r, setR] = useState(null);
  const [purgando, setPurgando] = useState(false);

  const correr = async () => {
    setCorriendo(true);
    setR(null);
    try {
      const { data } = await ensayoAPI.correr(tenantId, capsuleId, agente.agent_id, mensaje);
      setR(data);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo ensayar');
    } finally {
      setCorriendo(false);
    }
  };

  const purgar = async () => {
    setPurgando(true);
    try {
      const { data } = await ensayoAPI.purgar(tenantId, capsuleId, agente.agent_id);
      const total = Object.values(data.purgado || {}).reduce((a, b) => a + b, 0);
      toast.success(`Borrados ${total} registros de ensayo`);
      setR(null);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo purgar');
    } finally {
      setPurgando(false);
    }
  };

  const salud = r?.salud || {};
  const sano = salud.estado === 'ok';

  return (
    <Card className="rounded-sm border-brand-border" data-testid="ensayar-agente">
      <CardHeader className="py-2 px-3 border-b border-brand-border flex-row items-center justify-between">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600 flex items-center gap-1">
          <FlaskConical className="w-3 h-3" /> Ensayar a {agente?.name || agente?.agent_id}
        </CardTitle>
        <Button size="sm" variant="ghost" data-testid="purgar-ensayos" disabled={purgando}
                className="h-6 text-[10px] text-zinc-500 gap-1" onClick={purgar}>
          {purgando ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          Borrar lo que dejaron los ensayos
        </Button>
      </CardHeader>

      <CardContent className="p-3 space-y-3">
        <p className="text-[10px] text-zinc-500">
          Corre un turno de verdad con las skills y herramientas que <b>este</b> agente tiene
          activas. No le avisa a nadie: los correos quedan silenciados antes de mostrarte nada.
        </p>

        <textarea
          data-testid="ensayo-mensaje" value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
          className="w-full h-20 border border-brand-border rounded-sm p-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
          placeholder="Escribi lo que escribiria una persona. Por ejemplo: ¿que temperatura hace en Cordoba?"
        />
        <Button size="sm" data-testid="ensayo-correr" disabled={corriendo || !mensaje.trim()}
                className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3"
                onClick={correr}>
          {corriendo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FlaskConical className="w-3 h-3 mr-1" />}
          {corriendo ? 'Corriendo el turno…' : 'Ensayar'}
        </Button>

        {r && (
          <div className="space-y-3">
            <Bloque titulo="Lo que leeria la persona">
              <div data-testid="ensayo-respuesta" className="text-xs whitespace-pre-wrap text-[#09090B]">
                {r.respuesta || <span className="text-zinc-400">(no respondio nada)</span>}
              </div>
            </Bloque>

            {/* Primero las herramientas: cuando algo no anda, es lo que se necesita ver.
                La respuesta sola no dice por que una tool no se disparo. */}
            <Bloque titulo={`Herramientas que uso (${(r.herramientas || []).length})`}>
              {(r.herramientas || []).length === 0 && (
                <div className="text-[11px] text-zinc-500">
                  Ninguna. Si esperabas que usara una, mira abajo si la skill le llego y si le
                  quedaban rondas.
                </div>
              )}
              {(r.herramientas || []).map((t, i) => (
                <div key={i} data-testid={`ensayo-tool-${i}`} className="text-[11px] flex gap-2 items-start">
                  {t.ok ? <CheckCircle2 className="w-3 h-3 text-green-600 shrink-0 mt-0.5" />
                        : <XCircle className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <span className="font-mono font-semibold">{t.tool}</span>
                    <span className="text-zinc-500 font-mono"> {JSON.stringify(t.params)}</span>
                    {!t.ok && <div className="text-red-700">{t.error}</div>}
                  </div>
                </div>
              ))}
            </Bloque>

            <Bloque titulo="Skills que recibio">
              <div className="flex flex-wrap gap-1">
                {(r.skills_disponibles || []).map((s) => (
                  <Badge key={s.skill_id} variant="outline" className="text-[9px] rounded-sm">
                    {s.skill_id} · {(s.herramientas || []).length} tools
                  </Badge>
                ))}
              </div>
            </Bloque>

            <Bloque titulo={`Salud del turno: ${salud.estado || '?'}`}>
              <div className={`text-[11px] flex gap-2 ${sano ? 'text-green-800' : 'text-amber-800'}`}>
                {sano ? <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" /> : <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />}
                <div>
                  {(salud.avisos || []).length === 0 && <div>Sin señales de degradacion.</div>}
                  {(salud.avisos || []).map((a, i) => (
                    <div key={i}><span className="font-mono text-[10px] opacity-60">{a.codigo}</span> — {a.mensaje}</div>
                  ))}
                  {/* Los presupuestos explican la mitad de los "no uso la herramienta": el
                      agente se quedo sin rondas antes de llegar a ella. */}
                  <div className="text-[10px] text-zinc-500 font-mono mt-1">
                    {JSON.stringify(salud.presupuestos || {})}
                  </div>
                </div>
              </div>
            </Bloque>

            <Bloque titulo="Que quedo">
              <Quedo q={r.que_quedo || {}} />
            </Bloque>

            <div className="text-[10px] text-zinc-500 border border-brand-border rounded-sm p-2 flex gap-2">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              <div>{r.no_prueba}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Bloque({ titulo, children }) {
  return (
    <div className="border border-brand-border rounded-sm">
      <div className="px-2 py-1 border-b border-brand-border text-[10px] uppercase tracking-[0.1em] text-zinc-500">
        {titulo}
      </div>
      <div className="p-2 space-y-1">{children}</div>
    </div>
  );
}

function Quedo({ q }) {
  return (
    <div className="text-[11px] space-y-0.5">
      <div><span className="text-zinc-500">Atendio:</span> <span className="font-mono">{q.agente_que_atendio || '—'}</span></div>
      {!q.ticket && <div className="text-zinc-500">No creo ningun caso.</div>}
      {q.ticket && (
        <>
          <div>
            <span className="text-zinc-500">Caso:</span>{' '}
            <span className="font-mono">{q.ticket.ticket_id}</span> — {q.ticket.titulo}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono">
            intent={q.ticket.intent} · cat={q.ticket.categoria} · prio={q.ticket.prioridad} · rol={q.ticket.rol}
          </div>
          {(q.tareas || []).map((t, i) => (
            <div key={i} className="text-[10px]">
              tarea [{t.tipo}] {t.titulo} → <span className="font-mono">{t.asignada_a || 'sin asignar'}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
